import React, { useEffect, useMemo, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Search, Phone, Copy, BookUser, Globe2, MapPin, ExternalLink, ShieldCheck, Loader2, Building2 } from "lucide-react";
import { Dial8x8Button } from "@/components/Dial8x8";

const CAT_COLOR = {
  "PSAP / ECC": "#22D3EE", "State Routing": "#E0B23C", "Federal Law Enforcement": "#3B82F6",
  "Child Protection": "#F472B6", "Adult / Elder Protection": "#A78BFA", "Human Trafficking": "#EF4444",
  "Emergency Communications": "#4ade80", "PSAP Registry": "#8299bd", "Gaming Enforcement": "#F59E0B",
};

// AI-seeded MD/PA public-safety facility metadata (types match backend FACILITY_TYPES).
const FAC_META = {
  hospital: { label: "Hospital", color: "#EF4444" },
  fire_station: { label: "Fire Station", color: "#F97316" },
  police_station: { label: "Police Station", color: "#3B82F6" },
  ems_station: { label: "EMS Station", color: "#10B981" },
  school: { label: "School", color: "#F59E0B" },
  federal_building: { label: "Federal Building", color: "#A78BFA" },
};

// Nationwide PSAP result card
function PsapCard({ r, onCopy }) {
  const color = CAT_COLOR[r.category] || "#8299bd";
  return (
    <div className="border border-[#232838] rounded-lg p-3 bg-[#10141f] flex flex-col gap-1.5" data-testid={`psap-card-${(r.name || "").replace(/[^a-z0-9]/gi, "").slice(0, 30).toLowerCase()}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-semibold leading-snug">{r.name}</div>
          <div className="font-mono text-[10px] mt-0.5 flex items-center gap-1.5 flex-wrap" style={{ color }}>
            <span className="border px-1 py-px rounded-sm uppercase" style={{ borderColor: `${color}55` }}>{r.category}</span>
            {r.subtype && <span className="text-[#8299bd]">{r.subtype}</span>}
          </div>
        </div>
        <span className="font-mono text-[10px] font-bold text-[#8299bd] flex items-center gap-1 shrink-0"><MapPin size={10} /> {r.state}</span>
      </div>
      {(r.county || r.coverage) && <div className="font-mono text-[10px] text-[#5f7396]">{r.county ? `${r.county} · ` : ""}{r.coverage}</div>}
      {(r.phones || []).length > 0 ? (
        <div className="flex flex-col gap-1 mt-0.5">
          {r.phones.map((p) => (
            <div key={p.label + p.number} className="flex items-center gap-2">
              <span className="font-mono text-[9px] uppercase text-[#5f7396] w-24 shrink-0">{p.label}</span>
              <a href={`tel:${p.number.replace(/[^\d+]/g, "")}`} data-testid={`psap-call-${p.number.replace(/[^\d]/g, "")}`}
                className="font-mono text-sm text-[#4ade80] flex items-center gap-1.5 hover:underline"><Phone size={12} /> {p.number}</a>
              <Dial8x8Button phone={p.number} targetName={r.name} compact />
              <button data-testid={`psap-copy-${p.number.replace(/[^\d]/g, "")}`} onClick={() => onCopy(p.number)} className="text-[#6B7280] hover:text-white" aria-label="Copy number"><Copy size={12} /></button>
            </div>
          ))}
        </div>
      ) : (
        <div className="font-mono text-[10px] text-[#5f7396] italic">No published direct line — use registry / website</div>
      )}
      {r.notes && <div className="font-mono text-[10px] text-[#8299bd] leading-relaxed">{r.notes}</div>}
      <div className="flex items-center gap-3 mt-auto pt-1">
        {r.verified && <span className="font-mono text-[9px] text-[#4ade80] flex items-center gap-1"><ShieldCheck size={10} /> {r.verified}</span>}
        {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="font-mono text-[9px] text-[#22D3EE] flex items-center gap-1 hover:underline ml-auto"><ExternalLink size={10} /> Source</a>}
      </div>
    </div>
  );
}

// Shared PSAP / public-safety telephone directory: NATIONWIDE search + LOCAL quick dial.
export default function AgencyDirectory() {
  const [tab, setTab] = useState("nationwide");
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [category, setCategory] = useState("");
  const [filters, setFilters] = useState({ states: [], categories: [], total: 0 });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [facType, setFacType] = useState("");
  const [facState, setFacState] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    client.get("/psap/filters").then((r) => setFilters(r.data)).catch(() => {});
    client.get("/directory").then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab !== "facilities") return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      client.get("/facilities", { params: { q, type: facType, state: facState } })
        .then((r) => setFacilities(r.data.facilities)).catch(() => {});
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [tab, q, facType, facState]);

  useEffect(() => {
    if (tab !== "nationwide") return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await client.get("/psap/search", { params: { q, state, category, limit: 90 } });
        setResults(data.results);
      } catch { /* transient */ }
      setLoading(false);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [q, state, category, tab]);

  const copy = async (p) => {
    try {
      await navigator.clipboard.writeText(p);
      toast.success(`Copied ${p}`);
    } catch {
      // Clipboard permission denied / insecure context — fall back to a manual prompt.
      window.prompt("Copy number:", p);
    }
  };

  const filteredLocal = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return groups;
    return groups
      .map((g) => ({ ...g, contacts: g.contacts.filter((c) => `${c.name} ${c.phone}`.toLowerCase().includes(term)) }))
      .filter((g) => g.contacts.length);
  }, [groups, q]);

  return (
    <div className="h-full flex flex-col bg-[#0d1017]" data-testid="agency-directory">
      <div className="shrink-0 px-4 py-3 border-b border-[#232838] flex items-center gap-2 flex-wrap">
        <BookUser size={18} className="text-[#E0B23C]" />
        <div className="font-head font-bold tracking-tight">PSAP TELEPHONE DIRECTORY</div>
        <div className="flex items-center gap-1 ml-2">
          <button data-testid="directory-tab-nationwide" onClick={() => setTab("nationwide")}
            className={`px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wide rounded-sm flex items-center gap-1.5 ${tab === "nationwide" ? "bg-[#2563EB] text-white" : "text-[#8299bd] border border-[#232838]"}`}>
            <Globe2 size={11} /> Nationwide ({filters.total})
          </button>
          <button data-testid="directory-tab-local" onClick={() => setTab("local")}
            className={`px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wide rounded-sm ${tab === "local" ? "bg-[#2563EB] text-white" : "text-[#8299bd] border border-[#232838]"}`}>
            Local Quick Dial
          </button>
          <button data-testid="directory-tab-facilities" onClick={() => setTab("facilities")}
            className={`px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wide rounded-sm flex items-center gap-1.5 ${tab === "facilities" ? "bg-[#2563EB] text-white" : "text-[#8299bd] border border-[#232838]"}`}>
            <Building2 size={11} /> Facilities MD/PA
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {tab === "nationwide" && (
            <>
              <select data-testid="directory-state-filter" value={state} onChange={(e) => setState(e.target.value)}
                className="bg-[#141824] border border-[#232838] px-2 py-1.5 text-xs font-mono text-white outline-none focus:border-[#3B82F6]">
                <option value="">All states / territories</option>
                {filters.states.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="directory-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
                className="bg-[#141824] border border-[#232838] px-2 py-1.5 text-xs font-mono text-white outline-none focus:border-[#3B82F6]">
                <option value="">All categories</option>
                {filters.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </>
          )}
          {tab === "facilities" && (
            <>
              <select data-testid="facilities-type-filter" value={facType} onChange={(e) => setFacType(e.target.value)}
                className="bg-[#141824] border border-[#232838] px-2 py-1.5 text-xs font-mono text-white outline-none focus:border-[#3B82F6]">
                <option value="">All types</option>
                {Object.entries(FAC_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
              <select data-testid="facilities-state-filter" value={facState} onChange={(e) => setFacState(e.target.value)}
                className="bg-[#141824] border border-[#232838] px-2 py-1.5 text-xs font-mono text-white outline-none focus:border-[#3B82F6]">
                <option value="">MD + PA</option>
                <option value="MD">Maryland</option>
                <option value="PA">Pennsylvania</option>
              </select>
            </>
          )}
          <div className="flex items-center gap-2 bg-[#141824] border border-[#232838] px-2.5 py-1.5 focus-within:border-[#3B82F6] w-64">
            {loading ? <Loader2 size={13} className="text-[#3B82F6] animate-spin" /> : <Search size={13} className="text-[#6B7280]" />}
            <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={tab === "nationwide" ? "Agency, county, state, phone…" : "Search agency or number…"}
              className="flex-1 bg-transparent outline-none text-sm font-mono text-white placeholder:text-[#6B7280]" />
          </div>
        </div>
      </div>

      {tab === "nationwide" ? (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="psap-results">
            {results.map((r, i) => <PsapCard key={`${r.name}-${r.state}-${i}`} r={r} onCopy={copy} />)}
          </div>
          {!loading && results.length === 0 && (
            <div className="font-mono text-sm text-[#8299bd] mt-4">No agencies match{q ? ` “${q}”` : ""}{state ? ` in ${state}` : ""}. Try fewer filters.</div>
          )}
        </div>
      ) : tab === "facilities" ? (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="facilities-results">
            {facilities.map((f) => {
              const m = FAC_META[f.facility_type] || { label: f.facility_type, color: "#8299bd" };
              return (
                <div key={f.id} className="border border-[#232838] rounded-lg p-3 bg-[#10141f] flex flex-col gap-1.5" data-testid={`facility-card-${f.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white font-semibold leading-snug">{f.name}</div>
                      <div className="font-mono text-[10px] mt-0.5" style={{ color: m.color }}>
                        <span className="border px-1 py-px rounded-sm uppercase" style={{ borderColor: `${m.color}55` }}>{m.label}</span>
                      </div>
                    </div>
                    <span className="font-mono text-[10px] font-bold text-[#8299bd] flex items-center gap-1 shrink-0"><MapPin size={10} /> {f.state}</span>
                  </div>
                  <div className="font-mono text-[10px] text-[#5f7396]">{f.address}{f.city ? `, ${f.city}` : ""}{f.zip ? ` ${f.zip}` : ""}{f.county ? ` · ${f.county} Co.` : ""}</div>
                  {f.phone ? (
                    <div className="flex items-center gap-2 mt-0.5">
                      <a href={`tel:${f.phone.replace(/[^\d+]/g, "")}`} data-testid={`facility-call-${f.phone.replace(/[^\d]/g, "")}`}
                        className="font-mono text-sm text-[#4ade80] flex items-center gap-1.5 hover:underline"><Phone size={12} /> {f.phone}</a>
                      <Dial8x8Button phone={f.phone} targetName={f.name} compact />
                      <button onClick={() => copy(f.phone)} className="text-[#6B7280] hover:text-white" aria-label="Copy number"><Copy size={12} /></button>
                    </div>
                  ) : (
                    <div className="font-mono text-[10px] text-[#5f7396] italic">No published phone — verify before transfer</div>
                  )}
                  {f.ai_seeded && <div className="font-mono text-[9px] text-[#7C3AED] mt-auto pt-1">AI-SEEDED · GEMINI WEB SEARCH — verify before operational use</div>}
                </div>
              );
            })}
          </div>
          {facilities.length === 0 && (
            <div className="font-mono text-sm text-[#8299bd] mt-2">No facilities{q ? ` match “${q}”` : " seeded yet"} — an admin can run AI FACILITY SEED from the Admin console.</div>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredLocal.map((g) => (
            <div key={g.category} className="border border-[#232838] rounded-lg overflow-hidden" data-testid={`directory-group-${g.category.replace(/[^a-z]/gi, "").toLowerCase()}`}>
              <div className="px-3 py-2 bg-[#141824] font-head text-[11px] font-bold tracking-wide text-[#8299bd] uppercase">{g.category}</div>
              <div className="divide-y divide-[#1a2130]">
                {g.contacts.map((c) => (
                  <div key={c.name} className="px-3 py-2.5 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white leading-tight">{c.name}</div>
                      {c.note && <div className="font-mono text-[10px] text-[#8299bd]">{c.note}</div>}
                    </div>
                    <a href={`tel:${c.phone.replace(/[^\d+]/g, "")}`} data-testid={`directory-call-${c.phone.replace(/[^\d]/g, "")}`}
                      className="font-mono text-sm text-[#4ade80] flex items-center gap-1.5 hover:underline whitespace-nowrap"><Phone size={13} /> {c.phone}</a>
                    <Dial8x8Button phone={c.phone} targetName={c.name} compact />
                    <button data-testid={`directory-copy-${c.phone.replace(/[^\d]/g, "")}`} onClick={() => copy(c.phone)} className="text-[#6B7280] hover:text-white" aria-label="Copy number"><Copy size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {filteredLocal.length === 0 && <div className="font-mono text-sm text-[#8299bd]">No contacts match “{q}”.</div>}
        </div>
      )}

      <div className="shrink-0 px-4 py-2 border-t border-[#232838] font-mono text-[10px] text-[#5B6472]">
        National PSAP Contact Directory v2026-08-09 · Dial 911 for emergencies · Public/admin numbers are NOT validated 24x7 transfer lines — verify via NENA EPRC before out-of-area emergency transfer.
      </div>
    </div>
  );
}
